import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChatComposer } from './ChatComposer';
import { ChatMessageList } from './ChatMessageList';
import { ChatThreadList } from './ChatThreadList';
import { GlobalButton } from './GlobalButton';
import {
  INVESTMENT_CASE_LEGACY_THREAD_ID,
  useInvestmentCaseChatThreads,
} from '../hooks/useInvestmentCaseChat';
import {
  chatWithInvestmentCase,
  chatWithInvestmentCaseErrorMessage,
} from '../lib/chatWithInvestmentCase';
import type { ChatMessage, InvestmentCaseChatMessage } from '../types';
import styles from './InvestmentCaseChatPanel.module.css';

const PENDING_ASSISTANT_ID = 'pending-assistant';

export type InvestmentCaseChatPanelProps = {
  userId: string;
  caseId: string;
  caseName: string;
};

function toChatMessage(message: InvestmentCaseChatMessage): ChatMessage {
  return {
    id: message.id,
    userId: message.userId,
    threadId: message.threadId ?? message.caseId,
    role: message.role,
    content: message.content,
    createdAt: message.createdAt,
  };
}

export function InvestmentCaseChatPanel({
  userId,
  caseId,
  caseName,
}: InvestmentCaseChatPanelProps) {
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const chat = useInvestmentCaseChatThreads(userId, caseId, activeThreadId);
  const [composerDraft, setComposerDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [optimisticUser, setOptimisticUser] = useState<ChatMessage | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [retryMessage, setRetryMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (activeThreadId || chat.threadsLoading || chat.threads.length === 0) return;
    setActiveThreadId(chat.threads[0].id);
  }, [activeThreadId, chat.threads, chat.threadsLoading]);

  useEffect(() => {
    if (!optimisticUser) return;
    const persisted = chat.messages.some(
      (message) => message.role === 'user' && message.content === optimisticUser.content
    );
    if (persisted) {
      setOptimisticUser(null);
    }
  }, [chat.messages, optimisticUser]);

  const displayMessages = useMemo(() => {
    let items = chat.messages.map(toChatMessage);

    if (optimisticUser) {
      const hasPersisted = items.some(
        (message) => message.role === 'user' && message.content === optimisticUser.content
      );
      if (!hasPersisted) {
        items = [...items, optimisticUser];
      }
    }

    if (sending) {
      items = [
        ...items,
        {
          id: PENDING_ASSISTANT_ID,
          userId,
          threadId: activeThreadId ?? caseId,
          role: 'assistant',
          content: 'Thinking…',
          createdAt: new Date().toISOString(),
        },
      ];
    }

    return items;
  }, [activeThreadId, caseId, chat.messages, optimisticUser, sending, userId]);

  const resetTransientState = useCallback(() => {
    setSendError(null);
    setRetryMessage(null);
    setOptimisticUser(null);
    setComposerDraft('');
  }, []);

  const handleNewChat = useCallback(async () => {
    if (creating) return;
    setCreating(true);
    setActionError(null);

    try {
      const thread = await chat.createThread();
      setActiveThreadId(thread.id);
      resetTransientState();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not start a new chat');
    } finally {
      setCreating(false);
    }
  }, [chat, creating, resetTransientState]);

  const handleSelectThread = useCallback(
    (threadId: string) => {
      if (threadId === activeThreadId) return;
      setActiveThreadId(threadId);
      resetTransientState();
    },
    [activeThreadId, resetTransientState]
  );

  const activeThread = chat.threads.find((thread) => thread.id === activeThreadId) ?? null;

  const handleDeleteActiveChat = useCallback(async () => {
    if (!activeThreadId || deleting) return;

    const title = activeThread?.title ?? 'this chat';
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;

    setDeleting(true);
    setActionError(null);
    resetTransientState();

    const deletedId = activeThreadId;

    try {
      await chat.deleteThread(deletedId);
      const nextThread = chat.threads.find((thread) => thread.id !== deletedId);
      setActiveThreadId(nextThread?.id ?? null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not delete chat');
    } finally {
      setDeleting(false);
    }
  }, [activeThread?.title, activeThreadId, chat, deleting, resetTransientState]);

  const handleSend = useCallback(
    async (message: string) => {
      const trimmed = message.trim();
      if (!userId || !trimmed || sending) return;

      let threadId = activeThreadId;

      setSendError(null);
      setRetryMessage(null);
      setComposerDraft('');
      setSending(true);

      try {
        if (!threadId) {
          const thread = await chat.createThread();
          threadId = thread.id;
          setActiveThreadId(thread.id);
        }

        setOptimisticUser({
          id: `optimistic-user-${Date.now()}`,
          userId,
          threadId,
          role: 'user',
          content: trimmed,
          createdAt: new Date().toISOString(),
        });

        await chatWithInvestmentCase(
          caseId,
          trimmed,
          threadId === INVESTMENT_CASE_LEGACY_THREAD_ID ? undefined : threadId
        );
        setOptimisticUser(null);
      } catch (err) {
        setOptimisticUser(null);
        setSendError(chatWithInvestmentCaseErrorMessage(err));
        setRetryMessage(trimmed);
        setComposerDraft(trimmed);
      } finally {
        setSending(false);
      }
    },
    [activeThreadId, caseId, chat, sending, userId]
  );

  const handleComposerSubmit = useCallback(() => {
    void handleSend(composerDraft);
  }, [composerDraft, handleSend]);

  const handleRetrySend = useCallback(() => {
    if (!retryMessage) return;
    void handleSend(retryMessage);
  }, [handleSend, retryMessage]);

  return (
    <div className={styles.panel}>
      <div className={styles.threadColumn}>
        <ChatThreadList
          threads={chat.threads}
          activeThreadId={activeThreadId}
          loading={chat.threadsLoading}
          error={chat.error ?? actionError}
          creating={creating}
          onSelectThread={handleSelectThread}
          onNewChat={() => void handleNewChat()}
        />
      </div>

      <div className={styles.conversation}>
        <div className={styles.toolbar}>
          <GlobalButton
            type="button"
            variant="secondary"
            autoWidth
            isLoading={creating}
            disabled={creating}
            onClick={() => void handleNewChat()}
          >
            New chat
          </GlobalButton>
          <GlobalButton
            type="button"
            variant="secondary"
            destructive
            autoWidth
            aria-label={activeThread ? `Delete chat: ${activeThread.title}` : 'Delete chat'}
            disabled={!activeThreadId || deleting}
            isLoading={deleting}
            onClick={() => void handleDeleteActiveChat()}
          >
            Delete chat
          </GlobalButton>
        </div>

        {!chat.messagesLoading && displayMessages.length === 0 ? (
          <p className={`${styles.empty} muted`}>
            No messages yet — ask anything about {caseName}: DD scores, deck claims, founder fit,
            risks, or how it compares to your portfolio.
          </p>
        ) : (
          <ChatMessageList messages={displayMessages} loading={chat.messagesLoading} />
        )}

        {sendError ? (
          <div className={styles.sendError} role="alert">
            <p className="form-error">{sendError}</p>
            <GlobalButton
              type="button"
              variant="secondary"
              autoWidth
              disabled={sending}
              isLoading={sending}
              onClick={handleRetrySend}
            >
              Try again
            </GlobalButton>
          </div>
        ) : null}

        <div className={styles.composerDock}>
          <ChatComposer
            value={composerDraft}
            onChange={setComposerDraft}
            onSend={handleComposerSubmit}
            loading={sending}
            placeholder="Ask about this deal — risks, claims, founder fit…"
          />
        </div>
      </div>
    </div>
  );
}
